import React, {useState, useEffect} from 'react';
import {alumnosAPI, instrumentosAPI} from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';
import Modal from '../Common/Modal';
import StudentForm from '../Forms/StudentForm';
import SearchFilter from '../Common/SearchFilter';
import DataTable from '../Common/DataTable';
import StudentClassesModal from '../Common/StudentClassesModal';
import {PencilIcon, TrashIcon, UserPlusIcon, ClipboardDocumentIcon, InformationCircleIcon, AcademicCapIcon} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {useAuth} from '../../context/AuthContext';
import {copyToClipboard, filterBySearchTerm} from '../../services/utils';

const Students = ({currentYear}) => {
    const {isReadOnly} = useAuth();
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [selectedStudentForInfo, setSelectedStudentForInfo] = useState(null);
    const [isStudentInfoModalOpen, setIsStudentInfoModalOpen] = useState(false);
    const [instruments, setInstruments] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [enrolledIds, setEnrolledIds] = useState(new Set());

    useEffect(() => {
        fetchData();
    }, [currentYear]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [studentsRes, instrumentsRes, enrolledRes] = await Promise.all(
                [alumnosAPI.getAll(), instrumentosAPI.getAll(), alumnosAPI.getByYear(currentYear)]
            );

            setStudents(studentsRes.data);
            setEnrolledIds(new Set(enrolledRes.data.map(s => Number(s.IDAlumno))));

            // Crear mapa de instrumentos para búsqueda rápida
            const instrumentsMap = {};
            instrumentsRes
                .data
                .forEach(instrument => {
                    instrumentsMap[instrument.IDInstrumento] = instrument.Nombre;
                });
            setInstruments(instrumentsMap);
            setFilteredStudents(filterStudentsByTerm(studentsRes.data, searchTerm, instrumentsMap));

            setError(null);
        } catch (err) {
            setError('Error al cargar los datos');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filterStudentsByTerm = (studentsData, term, instrumentsMap = instruments) => {
        return filterBySearchTerm(studentsData, term, (student, lowerTerm) => {
            const instrumentName = instrumentsMap[student.InstrumentoID1] || '';
            return (
                student.Nombre.toLowerCase().includes(lowerTerm) || student.Apellido1.toLowerCase().includes(lowerTerm) || student.Apellido2
                    ?.toLowerCase().includes(lowerTerm) || student.Email1.toLowerCase().includes(lowerTerm) || student.Telefono1.includes(
                    lowerTerm
                ) || instrumentName.toLowerCase().includes(lowerTerm)
            );
        });
    };

    const handleSearch = (searchTerm) => {
        setSearchTerm(searchTerm);
        setFilteredStudents(filterStudentsByTerm(students, searchTerm));
    };

    const handleAdd = () => {
        setSelectedStudent(null);
        setIsModalOpen(true);
    };

    const handleEdit = (student) => {
        setSelectedStudent(student);
        setIsModalOpen(true);
    };

    const handleOpenInfo = (student) => {
        setSelectedStudentForInfo(student);
        setIsStudentInfoModalOpen(true);
    };

    const handleCloseInfo = () => {
        setIsStudentInfoModalOpen(false);
        setSelectedStudentForInfo(null);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar este alumno?')) {
            try {
                await alumnosAPI.delete(id);
                toast.success('Alumno eliminado correctamente');
                fetchData();
            } catch (err) {
                toast.error('Error al eliminar el alumno');
                console.error(err);
            }
        }
    };

    const handleToggleEnrollment = async (student) => {
        const enrolled = isEnrolled(student);
        try {
            if (enrolled) {
                await alumnosAPI.desmatricular(student.IDAlumno, currentYear);
                toast.success(`Alumno dado de baja del curso ${currentYear}`);
            } else {
                await alumnosAPI.matricular(student.IDAlumno, currentYear);
                toast.success(`Alumno matriculado en el curso ${currentYear}`);
            }
            fetchData();
        } catch (err) {
            toast.error('Error al actualizar la matrícula');
            console.error(err);
        }
    };

    const handleCopyEmails = () => {
        const emails = filteredStudents
            .filter(
                student => isEnrolled(student) && !student.F_Baja_ME
            )
            .map(student => student.Email1)
            .filter(email => email)
            .join('; ');

        if (!emails) {
            toast.error('No hay emails disponibles para el curso actual');
            return;
        }

        copyToClipboard(
            emails,
            () => toast.success('Emails copiados al portapapeles'),
            (error) => toast.error(error || 'Error al copiar los emails')
        );
    };

    const calculateAge = (birthDate) => {
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    const isEnrolled = (student) => enrolledIds.has(Number(student.IDAlumno));

    const handleSubmit = async (formData) => {
        try {
            // Validación adicional antes de enviar
            if (!formData.Nombre || !formData.Apellido1 || !formData.Email1 || !formData.Telefono1 || !formData.F_Nacimiento) {
                toast.error('Por favor complete todos los campos requeridos');
                return;
            }

            if (selectedStudent) {
                await alumnosAPI.update(selectedStudent.IDAlumno, formData);
                toast.success('Alumno actualizado correctamente');
            } else {
                await alumnosAPI.create(formData);
                toast.success('Alumno creado correctamente');
            }
            setIsModalOpen(false);
            fetchData();
        } catch (err) {
            const errorMessage = err.response
                ?.data
                    ?.message || 'Error al guardar el alumno';
            toast.error(errorMessage);
            console.error('Error al guardar el alumno:', err);
        }
    };

    const columns = [
        {
            key: 'Nombre',
            label: 'Nombre',
            sortable: true
        }, {
            key: 'Apellidos',
            label: 'Apellidos',
            sortable: true,
            sortValue: (student) => `${student.Apellido1 || ''} ${student.Apellido2 || ''} ${student.Nombre || ''}`,
            render: (value, student) => `${student.Apellido1} ${student.Apellido2 || ''}`.trim()
        }, {
            key: 'Edad',
            label: 'Edad',
            sortable: true,
            sortValue: (student) => student.F_Nacimiento ? calculateAge(student.F_Nacimiento) : -1,
            render: (value, student) => calculateAge(student.F_Nacimiento)
        }, {
            key: 'InstrumentoID1',
            label: 'Instrumento Principal',
            sortable: true,
            sortValue: (student) => instruments[student.InstrumentoID1] || '',
            render: (value) => instruments[value] || '-'
        }, {
            key: 'Telefono1',
            label: 'Teléfono'
        }, {
            key: 'Email1',
            label: 'Email',
            render: (value) => (
                <span className="block max-w-[24ch] truncate" title={value || ''}>
                    {value}
                </span>
            )
        }
    ];

    columns.push({
        key: 'actions',
        label: 'Acciones',
        render: (_, student) => (
            <div className="flex space-x-2">
                <button
                    onClick={() => handleOpenInfo(student)}
                    className="text-blue-600 hover:text-blue-900"
                    title="Info">
                    <InformationCircleIcon className="h-5 w-5"/>
                </button>
                {
                    !isReadOnly() && (
                        <>
                            <button
                                onClick={() => handleToggleEnrollment(student)}
                                className={isEnrolled(student) ? 'text-green-600 hover:text-green-900' : 'text-gray-400 hover:text-gray-700'}
                                title={isEnrolled(student) ? `Dar de baja del curso ${currentYear}` : `Matricular en el curso ${currentYear}`}>
                                <AcademicCapIcon className="h-5 w-5"/>
                            </button>
                            <button
                                onClick={() => handleEdit(student)}
                                className="text-primary-600 hover:text-primary-900"
                                title="Editar">
                                <PencilIcon className="h-5 w-5"/>
                            </button>
                            <button
                                onClick={() => handleDelete(student.IDAlumno)}
                                className="text-red-600 hover:text-red-900"
                                title="Eliminar">
                                <TrashIcon className="h-5 w-5"/>
                            </button>
                        </>
                    )
                }
            </div>
        )
    });

    if (loading) 
        return <LoadingSpinner text="Cargando alumnos..."/>;
    if (error) 
        return <ErrorMessage message={error} onRetry={fetchData}/>;
    
    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">Alumnos</h1>
                <div className="flex space-x-2">
                    <button
                        onClick={handleCopyEmails}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        disabled={filteredStudents.length === 0}>
                        <ClipboardDocumentIcon className="h-5 w-5 mr-2"/>
                        Copiar Emails
                    </button>
                    {
                        !isReadOnly() && (
                            <button onClick={handleAdd} className="btn-primary">
                                <UserPlusIcon className="h-5 w-5 mr-2"/>
                                Nuevo Alumno
                            </button>
                        )
                    }
                </div>
            </div>

            <SearchFilter
                onSearch={handleSearch}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Buscar por nombre, apellido, instrumento, email o teléfono..."/>

            <div className="card">
                <DataTable
                    columns={columns}
                    data={filteredStudents}
                    compact
                    emptyMessage="No hay alumnos registrados"
                    rowClassName={(
                        student) => (!isEnrolled(student) || student.F_Baja_ME)
                        ? 'opacity-60 text-gray-500'
                        : ''}/>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={selectedStudent
                    ? 'Editar Alumno'
                    : 'Nuevo Alumno'}
                size="xl">
                <StudentForm
                    student={selectedStudent}
                    onSubmit={handleSubmit}
                    onCancel={() => setIsModalOpen(false)}
                    currentYear={currentYear}/>
            </Modal>

            <StudentClassesModal
                isOpen={isStudentInfoModalOpen}
                onClose={handleCloseInfo}
                student={selectedStudentForInfo}
                currentYear={currentYear}/>
        </div>
    );
};

export default Students;
